import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  MenuItem,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  toastStateSelector,
  ToastSeverity,
  dialogState,
  visitListState,
} from 'state';
import { GeneralIds, ModelTypes } from 'types/types';
import { useParams } from 'react-router-dom';
import { mutation, query } from 'operations';
import { Visit } from 'api/models/Visit';

import { VisitTemplate } from 'api/models';
import { getDateTimeString } from 'utils';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import useRequest from 'hooks/useRequest';
import { DateTimePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';

export function AddVisitDialog() {
  const [{ data: visitData }, closeDialog] = useRecoilState(dialogState);
  const visit = visitData as Visit | undefined;
  const setToast = useSetRecoilState(toastStateSelector);
  const { t, i18n } = useTranslation();
  const setVisitList = useSetRecoilState(visitListState);
  const { patientId, projectId } = useParams<GeneralIds>();

  const { data } = useRequest<{
    visitTemplates: VisitTemplate[];
    index: number;
  }>(async function () {
    try {
      const { data: visits } = await query<Visit[]>({
        type: ModelTypes.visit,
        ids: {
          patientId,
        },
      });

      // finds max index
      const maxIndex = visits.reduce((acc, curr) => {
        if (curr.index > acc) {
          return curr.index;
        }
        return acc;
      }, 0);

      const { data: visitTemplates } = await query<VisitTemplate[]>({
        type: ModelTypes.visitTemplate,
        ids: { projectId },
      });

      return { visitTemplates, index: maxIndex + 1 };
    } catch (e: any) {
      const errorCode = e.response?.data?.error?.[0].code;
      setToast({
        isOpen: true,
        message: i18n.exists(`errors.${errorCode}`)
          ? t(`errors.${errorCode}`)
          : t('general.errors.save'),
        severity: ToastSeverity.error,
      });
    }
  });
  const { visitTemplates, index } = data ?? {};

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      date: visit?.date
        ? getDateTimeString(new Date(visit.date)).replaceAll('.', '-')
        : getDateTimeString(new Date()).replaceAll('.', '-'),
      visitTemplateId:
        visit?.visitTemplateId ?? visitTemplates?.length === 1
          ? visitTemplates?.[0].id
          : '',
    },
    validationSchema: yup.object({
      date: yup.string().required(t('general.errors.required') ?? ''),
      visitTemplateId: yup
        .string()
        .required(t('general.errors.required') ?? ''),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { date, visitTemplateId } = values;
      const visitTemplate = visitTemplates?.find(
        template => template.id === visitTemplateId,
      );

      let parsedDate = null;

      // @ts-ignore
      if (date.toDate) {
        // @ts-ignore
        parsedDate = date.toDate();
      } else {
        parsedDate = new Date(date);
      }

      try {
        await mutation<Visit>({
          type: ModelTypes.visit,
          body: {
            date: parsedDate,
            index: visit?.index ?? index,
            id: visit?.id ?? '',
            visitTemplateId,
          },
          ids: {
            patientId,
            id: visit?.id,
          },
        });

        const { data: visits } = await query<Visit[]>({
          type: ModelTypes.visit,
          ids: {
            patientId,
          },
        });
        setVisitList(visits);

        resetForm();
        closeDialog({ isOpen: false, data: null });

        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`, {
                maxNumber: visitTemplate?.repeatConfig,
              })
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  const onChangeDate = (date: Moment | null) => {
    formik.setFieldValue('date', date);
  };

  return (
    <Box>
      <DialogTitle>
        {visit ? t('dialogs.addVisit.editVisit') : t('dialogs.addVisit.title')}
      </DialogTitle>
      <DialogContent>
        <form>
          <DateTimePicker
            label={`${t('visits.visitDate')} *`}
            name="date"
            defaultValue={moment(formik.values.date)}
            value={moment(formik.values.date)}
            onChange={onChangeDate}
            onAccept={onChangeDate}
            ampm={false}
            format="YYYY.MM.DD HH:mm"
            slotProps={{
              textField: {
                fullWidth: true,
                helperText: formik.touched.date && formik.errors.date,
                error: formik.touched.date && Boolean(formik.errors.date),
                name: 'date',
                InputLabelProps: {
                  shrink: true,
                },
              },
            }}
            sx={{ mt: 2 }}
          />

          <ClearableSelect
            sx={{ mt: 2 }}
            fullWidth
            disabled={visit ? true : false}
            label={`${t('visits.visitTemplate')} *`}
            name="visitTemplateId"
            value={formik.values.visitTemplateId}
            onChange={formik.handleChange}
            error={
              formik.touched.visitTemplateId &&
              Boolean(formik.errors.visitTemplateId)
            }
            helperText={
              formik.touched.visitTemplateId && formik.errors.visitTemplateId
            }
          >
            {visitTemplates?.map(visitTemplate => (
              <MenuItem key={visitTemplate.id} value={visitTemplate.id}>
                {visitTemplate.name}
              </MenuItem>
            ))}
          </ClearableSelect>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={formik.isSubmitting}
          onClick={() => closeDialog({ isOpen: false, data: null })}
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
        >
          {t('general.cancel')}
        </Button>
        <Button
          disabled={formik.isSubmitting}
          type="submit"
          color="primary"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ mr: 2, mb: 1 }}
        >
          {t(getSaveButtonText(formik.isSubmitting, visit))}
        </Button>
      </DialogActions>
    </Box>
  );
}

function getSaveButtonText(isSaving: boolean, visit?: Visit) {
  if (isSaving) {
    return 'general.isSaving';
  }

  return visit ? 'general.save' : 'general.create';
}
